/**
 * BlogArticle组件 - 博客文章多图滑动展示组件
 * 基于原Vue的BlogArticle组件迁移，支持PC端和移动端不同布局
 */

'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { RightArrowOutlined } from '@zeelool/icon';
import Image from '@/components/Image';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper, SwiperSlide } from 'swiper/react';
import type {
  BlogArticleIndexData,
  BlogArticleItem,
  BlogArticleProps,
  BlogArticleTrackingParams,
} from './types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

// 获取alt属性的辅助函数
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  return indexData?.layout_title
    ? `${indexData.layout_title} ${componentName} ${index + 1}`
    : `${componentName} ${index + 1}`;
};

// 统一埋点参数Hook
const useTrackingParams = (
  item: BlogArticleItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): BlogArticleTrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: moduleId,
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: String(item.jump_type || ''),
      mkt_jump_content: String(item.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(item.sku?.shop_sku || ''),
      business_sku: String(item.sku?.business_sku || ''),
      product_sku: String(item.sku?.sku || ''),
      product_id: String(item.sku?.id || ''),
      product_name: String(item.sku?.short_name || ''),
      group_id: String(item.product_group?.id || ''),
      group_name: String(item.product_group?.name || ''),
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

// 统一埋点处理Hook
const useTrackingHandlers = (
  trackingParams: BlogArticleTrackingParams,
  onTrack?: (params: BlogArticleTrackingParams) => void,
  onExpTrack?: (params: BlogArticleTrackingParams) => void,
  onValidExpTrack?: (params: BlogArticleTrackingParams) => void,
) => {
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('BlogArticle exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('BlogArticle click tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

// 博客文章项组件 - PC端
const BlogArticleItemPC = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  moduleIndex,
  onValidExpTrack,
}: {
  item: BlogArticleItem;
  index: number;
  indexData: BlogArticleIndexData;
  onTrack?: (params: BlogArticleTrackingParams) => void;
  onExpTrack?: (params: BlogArticleTrackingParams) => void;
  onValidExpTrack?: (params: BlogArticleTrackingParams) => void;
  moduleIndex: number;
}) => {
  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData?.component_id || 'blogArticle',
    indexData?.layout_title || '',
    indexData?.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && { product_group: { id: String(item.product_group.id) } }),
      ...(item.crowd_recommend?.id && { crowd_recommend: { id: String(item.crowd_recommend.id) } }),
    }),
    [item],
  );
  const blogArticleRef = useMergedRefs(expRef, validExposureRef);
  return (
    <div ref={blogArticleRef} className="relative h-full">
      <MagicJump
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={item.title || getAlt('BlogArticle', index, indexData)}
        className="block h-full"
      >
        <div className="relative h-full flex flex-col justify-between items-start">
          {/* 图片和文字上部分 */}
          <div className="w-full flex-1">
            <Image
              src={item.jump_image || ''}
              alt={item.title || getAlt('BlogArticle', index, indexData)}
              width={130}
              height={130}
              loading={moduleIndex < 5 ? 'eager' : 'lazy'}
              className="w-[130px] h-auto object-cover"
              style={{
                aspectRatio: item.jump_img_ratio || 'auto',
              }}
            />

            {/* 标题 */}
            {item.title && (
              <h3
                className="w-full font-bb text-[16px] lg:text-[18px] 2xl:text-[20px] mt-[32px] truncate whitespace-pre-line"
                style={{ color: item.title_color || '#000000' }}
                dangerouslySetInnerHTML={{
                  __html: item.title,
                }}
              />
            )}

            {/* 描述 */}
            {item.des && (
              <h4
                className="w-full mt-[8px] text-[12px] lg:text-[14px] 2xl:text-[16px] leading-[18px] lg:leading-[20px] 2xl:leading-[24px] line-clamp-3 whitespace-pre-line break-words"
                style={{ color: item.des_color || '#000000' }}
                dangerouslySetInnerHTML={{
                  __html: item.des,
                }}
              />
            )}
          </div>

          {/* 按钮 */}
          {item.btn_text && (
            <div
              className="flex items-center justify-center min-w-[142px] w-fit font-bb text-[16px] h-[48px] rounded-[24px] px-[15px] box-border mt-[24px] mb-[2px] cursor-pointer border"
              style={{
                color: item.btn_word_color || '#000000',
                backgroundColor: item.btn_bg_color || 'transparent',
                borderColor: item.btn_word_color || '#000000',
              }}
            >
              <span>{item.btn_text}</span>
              <RightArrowOutlined className="ml-[8px] text-[10px]" />
            </div>
          )}
        </div>
      </MagicJump>
    </div>
  );
};

// 博客文章项组件 - 移动端
const BlogArticleItemMobile = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  moduleIndex,
  onValidExpTrack,
}: {
  item: BlogArticleItem;
  index: number;
  indexData: BlogArticleIndexData;
  onTrack?: (params: BlogArticleTrackingParams) => void;
  onExpTrack?: (params: BlogArticleTrackingParams) => void;
  onValidExpTrack?: (params: BlogArticleTrackingParams) => void;
  moduleIndex: number;
}) => {
  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData?.component_id || 'blogArticle',
    indexData?.layout_title || '',
    indexData?.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && { product_group: { id: String(item.product_group.id) } }),
      ...(item.crowd_recommend?.id && { crowd_recommend: { id: String(item.crowd_recommend.id) } }),
    }),
    [item],
  );

  const blogArticleRef = useMergedRefs(expRef, validExposureRef);
  return (
    <div
      ref={blogArticleRef}
      className="relative"
      style={{ backgroundColor: indexData.bg_color || 'transparent' }}
    >
      <div className="wrapper relative">
        <MagicJump
          item={jumpProps}
          onBeforeJump={handleClick}
          aria-label={item.title || getAlt('BlogArticle', index, indexData)}
          className="flex flex-col items-center justify-center py-[35px] px-[14px] h-[276px] box-border"
        >
          {/* 图片 */}
          <Image
            src={item.jump_image || ''}
            alt={getAlt('BlogArticle', index, indexData)}
            width={84}
            height={84}
            loading={moduleIndex < 4 ? 'eager' : 'lazy'}
            className="w-[84px] h-auto block"
            style={{
              aspectRatio: item.jump_img_ratio || 'auto',
            }}
          />

          {/* 文字信息 */}
          <div className="w-full box-border flex flex-col items-center mt-[20px]">
            {/* 标题 */}
            {item.title && (
              <h3
                className="font-bb text-[14px] truncate whitespace-pre-line w-full text-center"
                style={{ color: item.title_color || '#000000' }}
                dangerouslySetInnerHTML={{
                  __html: item.title,
                }}
              />
            )}

            {/* 描述 */}
            {item.des && (
              <h4
                className="w-full text-center mt-[4px] text-[12px] line-clamp-2 whitespace-pre-line"
                style={{ color: item.des_color || '#000000' }}
                dangerouslySetInnerHTML={{
                  __html: item.des,
                }}
              />
            )}

            {/* 按钮 */}
            {item.btn_text && (
              <button
                className="block mt-[12px] min-w-[145px] h-[40px] rounded-[20px] text-center leading-[40px] font-bb text-[14px] border"
                style={{
                  color: item.btn_word_color || '#000000',
                  backgroundColor: item.btn_bg_color || 'transparent',
                  borderColor: item.btn_word_color || '#000000',
                }}
              >
                {item.btn_text}
              </button>
            )}
          </div>
        </MagicJump>
      </div>
    </div>
  );
};

/**
 * BlogArticle主组件
 */
export default function BlogArticle({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: BlogArticleProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取跳转目标列表
  const jumpTargets = useMemo(() => {
    return indexData?.jump_target || [];
  }, [indexData?.jump_target]);

  // PC端Swiper配置 - 参考Vue项目的配置
  const pcSwiperOptions = useMemo(
    () => ({
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      spaceBetween: 0,
      allowTouchMove: true,
      breakpoints: {
        1024: {
          allowTouchMove: jumpTargets.length >= 4, // 数据少于4个时禁用滑动
        },
      },
    }),
    [jumpTargets.length],
  );

  // 移动端Swiper配置
  const mobileSwiperOptions = useMemo(
    () => ({
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 1.35,
      spaceBetween: 10,
      slidesOffsetBefore: 14,
      slidesOffsetAfter: 14,
    }),
    [],
  );

  // 根据设备类型选择配置
  const swiperOptions = isPC ? pcSwiperOptions : mobileSwiperOptions;

  // 容器样式
  const containerStyle = useMemo(() => {
    if (isPC) {
      return { backgroundColor: indexData.bg_color || '#EEEEEE' };
    }
    return {};
  }, [isPC, indexData.bg_color]);

  // 如果没有数据或数据不足，不渲染组件
  if (!indexData || !jumpTargets.length || jumpTargets.length <= 1) {
    return null;
  }

  return (
    <div
      className={`
        ${isPC ? 'py-[70px] pb-[112px] my-[30px] lg:my-[50px] 2xl:my-[80px]' : 'my-[40px]'}
      `}
      style={containerStyle}
    >
      {/* 标题组件 */}
      <HomePageTitle
        indexData={indexData}
        variant="default"
        titleTag="h2"
        subtitleTag="h4"
        hasButton={false}
      />

      {/* 轮播容器 */}
      <div className={`relative ${isPC ? 'mt-[54px]' : 'mt-[20px]'}`}>
        <Swiper
          {...swiperOptions}
          className={isPC ? '!px-[45px] lg:!px-[100px] 2xl:!px-[180px]' : ''}
        >
          {jumpTargets.map((item, index) => (
            <SwiperSlide
              key={item.id || index}
              className={
                isPC
                  ? '!w-[21.2%] !mr-[5%] last:!mr-0 !h-[auto] !min-w-[248px] lg:!min-w-[auto]' // PC端：1024px以后最小宽度248px
                  : ''
              }
            >
              {isPC ? (
                <BlogArticleItemPC
                  item={item}
                  index={index}
                  indexData={indexData}
                  onTrack={onTrack}
                  onExpTrack={onExpTrack}
                  onValidExpTrack={onValidExpTrack}
                  moduleIndex={moduleIndex}
                />
              ) : (
                <BlogArticleItemMobile
                  item={item}
                  index={index}
                  indexData={indexData}
                  onTrack={onTrack}
                  onExpTrack={onExpTrack}
                  onValidExpTrack={onValidExpTrack}
                  moduleIndex={moduleIndex}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
